import { ReactNode, useEffect, useState } from 'react'
import { Box, DialogContent } from '@mui/material'
import MUIDialog from '@mui/material/Dialog'
import classNames from 'classnames'
import { Formik, FormikProps } from 'formik'
import { t } from 'i18next'

import { AlertType } from 'src/components/shared/ui/AlertDialog/enums'
import ButtonPrimaryOutlined from 'src/components/shared/ui/ButtonPrimaryOutlined'
import ButtonSecondary from 'src/components/shared/ui/ButtonSecondary'
import { DialogStyles as styles } from 'src/components/shared/ui/Dialog/DialogStyles'
import DialogActions from 'src/components/shared/ui/DialogActions'
import DialogTitle from 'src/components/shared/ui/DialogTitle/DialogTitle'

type Props = {
  children: ReactNode
  title: string
  buttons?: ReactNode | null
  type?: AlertType
  isOpen?: boolean
  disabled?: boolean
  close: () => void
  accept?: () => void
  cancel: () => void
  acceptButtonTitle?: string
  cancelButtonTitle?: string
  titleIcon?: ReactNode | null
  withForm?: boolean
  validateFormik?: boolean
  formInitialValues?: any
  closeClickingOutsideDialogAndEscapeKey?: boolean
  formValidationScheme?: () => void
  formHandleSubmit?: (values) => void
}

function Dialog({
  title,
  children,
  close,
  cancel,
  accept = () => {
    return null
  },
  isOpen = false,
  disabled,
  acceptButtonTitle = t('Añadir'),
  cancelButtonTitle = t('Cancelar'),
  buttons = null,
  titleIcon = null,
  type = AlertType.DEFAULT,
  withForm = false,
  validateFormik = true,
  formInitialValues = null,
  closeClickingOutsideDialogAndEscapeKey = false,
  formValidationScheme = () => {
    return null
  },
  formHandleSubmit = () => {
    return null
  }
}: Props): JSX.Element {
  const dialogClassNames = classNames(type)
  const [validateAfterSubmit, setValidateAfterSubmit] = useState(validateFormik)

  useEffect(() => {
    if (!isOpen && !validateFormik) {
      setValidateAfterSubmit(false)
    }
  }, [isOpen])

  function returnResult(): boolean {
    if (!withForm) {
      close()
      accept()
    }
    return true
  }

  function cancelResult() {
    close()
    cancel()
  }

  function getAcceptButtonType() {
    if (withForm && formHandleSubmit !== null) {
      return 'submit'
    }
    return 'button'
  }

  function getDialogActions(formik: FormikProps<any> | null = null): ReactNode {
    if (buttons) {
      return buttons
    }

    let buttonDisabled = disabled

    if (validateFormik && formik !== null && !formik.isValid) {
      buttonDisabled = true
    }

    return (
      <>
        <ButtonPrimaryOutlined onClick={cancelResult}>{cancelButtonTitle}</ButtonPrimaryOutlined>
        <Box className="primary-button">
          <ButtonSecondary
            onClick={returnResult}
            disabled={buttonDisabled}
            type={getAcceptButtonType()}
          >
            {acceptButtonTitle}
          </ButtonSecondary>
        </Box>
      </>
    )
  }

  const handleSubmit = values => {
    if (formHandleSubmit !== null) {
      formHandleSubmit(values)
    }
  }

  const getDialogContentAndFooter = () => {
    if (!withForm) {
      return (
        <>
          <DialogContent>{children}</DialogContent>
          <DialogActions>{getDialogActions()}</DialogActions>
        </>
      )
    }
    return (
      <Formik
        initialValues={formInitialValues}
        validationSchema={formValidationScheme}
        onSubmit={handleSubmit}
        validateOnMount={validateAfterSubmit}
        validateOnBlur={false}
        validateOnChange={validateAfterSubmit}
      >
        {formik => (
          <form
            onSubmit={async event => {
              event.stopPropagation()
              event.preventDefault()

              const errors = await formik.validateForm()

              if (Object.keys(errors).length && Object.keys(errors).length > 0) {
                setValidateAfterSubmit(true)
              } else {
                setValidateAfterSubmit(false)
              }

              formik.handleSubmit(event)
            }}
          >
            <DialogContent>{children}</DialogContent>
            <DialogActions>{getDialogActions(formik)}</DialogActions>
          </form>
        )}
      </Formik>
    )
  }

  return (
    <MUIDialog
      className={dialogClassNames}
      sx={styles(type).root}
      onClose={closeClickingOutsideDialogAndEscapeKey ? close : undefined}
      open={isOpen}
      fullWidth
      maxWidth="md"
      fullScreen={false}
      container={document.body}
    >
      <DialogTitle
        className={dialogClassNames}
        id="destinity-dialog"
        // onClose={cancelResult}
        type={type}
        icon={titleIcon}
      >
        {title}
      </DialogTitle>
      {getDialogContentAndFooter()}
    </MUIDialog>
  )
}

export default Dialog
